import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './EditProjectDeliverableRevenue.css';
import IconButton from '@mui/material/IconButton';
import axiosInstance from '../../../Config/axiosInstance';
import Select from 'react-select';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { NumericFormat } from "react-number-format";

const formatDateLabel = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}.${month}.${year}`;
};

function EditProjectDeliverableRevenue() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    offerCode: '',
    projectCode: '',
    deliverableId: '',
    deliverableDescription: '',
    revenue_offer_without_vat: '',
    revenue_po_without_vat: '',
    currency: '',
    exchange_rate: '',
    selectedExchangeRate: null,
    revenue_offer_without_vat_system: '',
    revenue_po_without_vat_system: '',
  });

  const [offerCodes, setOfferCodes] = useState([]);
  const [projectCodes, setProjectCodes] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [exchangeRates, setExchangeRates] = useState([]);
  const [systemCurrencyId, setSystemCurrencyId] = useState(null);
  const [systemCurrencyLabel, setSystemCurrencyLabel] = useState("");

  const getExchangeOptionsForCurrency = useCallback((currencyId) => {
    return exchangeRates
      .filter(rate => Number(rate.currency) === Number(currencyId))
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .map(rate => ({
        value: rate.id,
        label: String(rate.value ?? ""),
        rateValue: Number(rate.value),
        date: rate.date,
      }));
  }, [exchangeRates]);

  const calculateSystemRevenue = useCallback((amount, currencyId, selectedExchangeRate) => {
    if (amount === "" || amount === null || amount === undefined) return "";

    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount)) return "";

    if (!currencyId || !systemCurrencyId) return "";

    if (Number(currencyId) === Number(systemCurrencyId)) {
      return numericAmount.toFixed(2);
    }

    if (!selectedExchangeRate?.rateValue) return "";

    return (numericAmount * selectedExchangeRate.rateValue).toFixed(2);
  }, [systemCurrencyId]);

  const refreshSystemAmounts = useCallback((data) => ({
    ...data,
    revenue_offer_without_vat_system: calculateSystemRevenue(
      data.revenue_offer_without_vat,
      data.currency,
      data.selectedExchangeRate
    ),
    revenue_po_without_vat_system: calculateSystemRevenue(
      data.revenue_po_without_vat,
      data.currency,
      data.selectedExchangeRate
    ),
  }), [calculateSystemRevenue]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!state?.id) {
          navigate('/project-budget/project-deliverable-revenue');
          return;
        }

        const offerCodesResponse = await axiosInstance.get('/offer-codes/');
        const formattedOfferCodes = offerCodesResponse.data.map(offer_code => ({
          value: offer_code.id,
          label: `${offer_code.offer_code} - ${offer_code.description}`,
          offer_code: offer_code.offer_code
        }));
        setOfferCodes(formattedOfferCodes);

        const projectCodesResponse = await axiosInstance.get('/project-codes/');
        const formattedProjectCodes = projectCodesResponse.data.map(project_code => ({
          value: project_code.id,
          label: `${project_code.code} - ${project_code.description}`,
          project_code: project_code.code
        }));
        setProjectCodes(formattedProjectCodes);

        const currenciesResponse = await axiosInstance.get("/currencies/");
        const formattedCurrencies = currenciesResponse.data.map(currency => ({
          value: currency.id,
          label: currency.abbreviation
        }));
        setCurrencyOptions(formattedCurrencies);

        const exchangeRatesResponse = await axiosInstance.get("/exchange-rate/");
        const loadedExchangeRates = exchangeRatesResponse.data || [];
        setExchangeRates(loadedExchangeRates);

        const systemCurrencyResponse = await axiosInstance.get("/system-currency/");
        const loadedSystemCurrencyId =
          Array.isArray(systemCurrencyResponse.data) && systemCurrencyResponse.data.length > 0
            ? systemCurrencyResponse.data[0]?.currency || null
            : null;

        setSystemCurrencyId(loadedSystemCurrencyId);

        const systemCurrency = formattedCurrencies.find(
          currency => Number(currency.value) === Number(loadedSystemCurrencyId)
        );
        setSystemCurrencyLabel(systemCurrency?.label || "");

        const budgetResponse = await axiosInstance.get(`/project-deliverable-revenue/${state.id}/`);
        const record = budgetResponse.data;

        const selectedOfferCode = formattedOfferCodes.find(code =>
          code.offer_code === record.offer_code || code.value === Number(record.offer_code)
        );

        const selectedProjectCode = formattedProjectCodes.find(code =>
          code.project_code === record.project_code || code.value === Number(record.project_code)
        );

        const selectedExchangeRate = record.exchange_rate
          ? loadedExchangeRates
              .filter(rate => Number(rate.currency) === Number(record.currency))
              .map(rate => ({
                value: rate.id,
                label: String(rate.value ?? ""),
                rateValue: Number(rate.value),
                date: rate.date,
              }))
              .find(rate => Number(rate.value) === Number(record.exchange_rate)) || null
          : null;

        const initialData = {
          offerCode: selectedOfferCode?.value || '',
          projectCode: selectedProjectCode?.value || '',
          deliverableId: record.deliverable_id || '',
          deliverableDescription: record.deliverable_description || '',
          revenue_offer_without_vat: record.revenue_offer_without_vat || "",
          revenue_po_without_vat: record.revenue_po_without_vat || "",
          currency: record.currency || "",
          exchange_rate: record.exchange_rate || "",
          selectedExchangeRate,
          revenue_offer_without_vat_system: record.revenue_offer_without_vat_system || "",
          revenue_po_without_vat_system: record.revenue_po_without_vat_system || "",
        };

        setFormData(refreshSystemAmounts(initialData));

      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [state?.id, navigate, refreshSystemAmounts]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev =>
      refreshSystemAmounts({
        ...prev,
        [name]: value
      })
    );
  };

  const handleSelectChange = (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : null;

    if (name === 'currency') {
      const isSystemCurrency =
        systemCurrencyId && Number(value) === Number(systemCurrencyId);

      const defaultExchangeRate = !isSystemCurrency && value
        ? getExchangeOptionsForCurrency(value)[0] || null
        : null;

      setFormData(prev =>
        refreshSystemAmounts({
          ...prev,
          currency: value || "",
          exchange_rate: defaultExchangeRate?.value || "",
          selectedExchangeRate: defaultExchangeRate,
        })
      );

      return;
    }

    if (name === 'exchange_rate') {
      setFormData(prev =>
        refreshSystemAmounts({
          ...prev,
          exchange_rate: value || "",
          selectedExchangeRate: selectedOption || null,
        })
      );

      return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    let newErrors = {};

    if (!formData.offerCode) newErrors.offerCode = 'Offer code is required.';
    if (!formData.deliverableId) newErrors.deliverableId = 'Deliverable Id is required.';
    if (!formData.deliverableDescription) newErrors.deliverableDescription = 'Deliverable description is required.';

    if (!formData.revenue_offer_without_vat && !formData.revenue_po_without_vat) {
      newErrors.revenue_offer_without_vat = 'Revenue is required.';
    }

    if (!formData.currency) {
      newErrors.currency = 'Currency is required.';
    }

    const selectedCurrencyIsSystem =
      formData.currency &&
      systemCurrencyId &&
      Number(formData.currency) === Number(systemCurrencyId);

    if (formData.currency && !selectedCurrencyIsSystem && !formData.exchange_rate) {
      newErrors.exchange_rate = 'Exchange rate is required.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const payload = {
        offer_code: formData.offerCode,
        project_code: formData.projectCode || null,
        deliverable_id: formData.deliverableId,
        deliverable_description: formData.deliverableDescription,
        revenue_offer_without_vat: parseFloat(formData.revenue_offer_without_vat) || 0,
        revenue_po_without_vat: parseFloat(formData.revenue_po_without_vat) || null,
        currency: formData.currency ? Number(formData.currency) : null,
        exchange_rate:
          formData.exchange_rate && Number(formData.currency) !== Number(systemCurrencyId)
            ? Number(formData.exchange_rate)
            : null,
      };

      await axiosInstance.put(`/project-deliverable-revenue/${state.id}/`, payload);
      navigate('/project-budget/project-deliverable-revenue');
    } catch (error) {
      console.error('Error saving data:', error.response?.data || error.message);
      alert(`Eroare la salvare: ${JSON.stringify(error.response?.data || error.message)}`);
    }
  };

  const handleDiscard = () => navigate('/project-budget/project-deliverable-revenue');
  const handleUndo = () => navigate(-1);

  const selectedCurrencyIsSystem =
    formData.currency &&
    systemCurrencyId &&
    Number(formData.currency) === Number(systemCurrencyId);

  const exchangeOptions = formData.currency
    ? getExchangeOptionsForCurrency(formData.currency)
    : [];

  return (
    <div className="edit-project-budget-page">
      <div className="edit-container">
        <div className="header">
          <IconButton onClick={handleUndo}>
            <ArrowBackIcon className="back-arrow" />
          </IconButton>
          <h2 className="edit-title">Edit project deliverable revenue</h2>
        </div>
      </div>

      <hr className="title-separator" />

      <div className="edit-container">
        <form onSubmit={handleSave} className="edit-form">

          <div className="form-group">
            <label htmlFor="offer_code" className="label">
              Offer Code <span style={{ color: 'red' }}>*</span>
            </label>
            <Select
              id="offer_code"
              name="offerCode"
              classNamePrefix="react-select"
              options={offerCodes}
              value={offerCodes.find(o =>
                o.value === Number(formData.offerCode) ||
                o.offer_code === formData.offerCode
              ) || null}
              onChange={e => handleSelectChange('offerCode', e)}
              isClearable
              required
              styles={{ control: (base) => ({ ...base, borderRadius: '8px' }) }}
            />
            {errors.offerCode && <div className="error">{errors.offerCode}</div>}
          </div>

          <div className="form-group">
            <label htmlFor="project_code" className="label">
              Project Code <span style={{ color: 'red' }}>*</span>
            </label>
            <Select
              id="project_code"
              name="projectCode"
              classNamePrefix="react-select"
              options={projectCodes}
              value={projectCodes.find(o =>
                o.value === Number(formData.projectCode) ||
                o.project_code === formData.projectCode
              ) || null}
              onChange={e => handleSelectChange('projectCode', e)}
              isClearable
              styles={{ control: (base) => ({ ...base, borderRadius: '8px' }) }}
            />
            {errors.projectCode && <div className="error">{errors.projectCode}</div>}
          </div>

          <div className="form-group-row">
            <div className="form-group-short">
              <label htmlFor="deliverable_id" className="label">
                Deliverable Id <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                id="deliverable_id"
                name="deliverableId"
                value={formData.deliverableId}
                onChange={handleChange}
                className="input"
                required
              />
              {errors.deliverableId && <div className="error">{errors.deliverableId}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="deliverable_description" className="label">
                Deliverable description <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                id="deliverable_description"
                name="deliverableDescription"
                value={formData.deliverableDescription}
                onChange={handleChange}
                className="input"
                required
              />
              {errors.deliverableDescription && <div className="error">{errors.deliverableDescription}</div>}
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="revenue_offer_without_vat" className="label">
                Revenue offer without VAT <span style={{ color: 'red' }}>*</span>
              </label>
              <NumericFormat
                id="revenue_offer_without_vat"
                name="revenue_offer_without_vat"
                value={formData.revenue_offer_without_vat}
                onValueChange={({ value }) =>
                  handleChange({
                    target: {
                      name: "revenue_offer_without_vat",
                      value: value || "",
                    },
                  })
                }
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                allowLeadingZeros={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                required
              />
              {errors.revenue_offer_without_vat && (
                <div className="error">{errors.revenue_offer_without_vat}</div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="revenue_po_without_vat" className="label">
                Revenue PO without VAT
              </label>
              <NumericFormat
                id="revenue_po_without_vat"
                name="revenue_po_without_vat"
                value={formData.revenue_po_without_vat}
                onValueChange={({ value }) =>
                  handleChange({
                    target: {
                      name: "revenue_po_without_vat",
                      value: value || "",
                    },
                  })
                }
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                allowLeadingZeros={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
              />
            </div>

            <div className="form-group">
              <label htmlFor="currency" className="label">
                Currency <span style={{ color: 'red' }}>*</span>
              </label>
              <Select
                id="currency"
                name="currency"
                classNamePrefix="react-select"
                options={currencyOptions}
                value={currencyOptions.find(s => Number(s.value) === Number(formData.currency)) || null}
                onChange={e => handleSelectChange('currency', e)}
                isClearable
                styles={{ control: (base) => ({ ...base, borderRadius: '8px' }) }}
              />
              {errors.currency && <div className="error">{errors.currency}</div>}
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label htmlFor="exchange_rate" className="label">
                Exchange Rate
                {!selectedCurrencyIsSystem && formData.currency ? (
                  <span style={{ color: 'red' }}> *</span>
                ) : null}
              </label>
              <Select
                id="exchange_rate"
                name="exchange_rate"
                classNamePrefix="react-select"
                options={exchangeOptions}
                value={formData.selectedExchangeRate}
                onChange={e => handleSelectChange('exchange_rate', e)}
                isClearable
                isDisabled={!formData.currency || selectedCurrencyIsSystem}
                placeholder={
                  !formData.currency
                    ? "Select currency first"
                    : selectedCurrencyIsSystem
                      ? "Not needed"
                      : exchangeOptions.length
                        ? "Select rate"
                        : "No rates"
                }
                formatOptionLabel={(option, { context }) =>
                  context === "menu"
                    ? `${option.label}${option.date ? ` - ${formatDateLabel(option.date)}` : ""}`
                    : option.label
                }
                styles={{ control: (base) => ({ ...base, borderRadius: '8px' }) }}
              />
              {errors.exchange_rate && <div className="error">{errors.exchange_rate}</div>}
            </div>

            <div className="form-group">
              <label htmlFor="revenue_offer_without_vat_system" className="label">
                Revenue offer without VAT System
                {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
              </label>
              <NumericFormat
                id="revenue_offer_without_vat_system"
                name="revenue_offer_without_vat_system"
                value={formData.revenue_offer_without_vat_system}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                readOnly
              />
            </div>

            <div className="form-group">
              <label htmlFor="revenue_po_without_vat_system" className="label">
                Revenue PO without VAT System
                {systemCurrencyLabel ? ` (${systemCurrencyLabel})` : ""}
              </label>
              <NumericFormat
                id="revenue_po_without_vat_system"
                name="revenue_po_without_vat_system"
                value={formData.revenue_po_without_vat_system}
                thousandSeparator="."
                decimalSeparator=","
                decimalScale={2}
                fixedDecimalScale
                allowNegative={false}
                inputMode="decimal"
                className="input"
                placeholder="0,00"
                readOnly
              />
            </div>
          </div>

          <div className="form-group-row">
            <div className="button-group-inline">
              <button type="button" className="button discard" onClick={handleDiscard}>
                Discard
              </button>
              <button type="submit" className="button save">
                Save
              </button>
            </div>
          </div>

        </form>
      </div>
    </div>
  );
}

export default EditProjectDeliverableRevenue;